import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Hoja } from "@/components/Hoja";
import { api, errorText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useRemanso } from "@/context/RemansoContext";
import { enablePush, disablePush, isIOS, isStandalone, pushSupported } from "@/lib/push";
import { promptInstall, usePwa } from "@/lib/pwa";

const TIMES = Array.from({ length: 96 }, (_, i) => `${String(Math.floor(i / 4)).padStart(2, "0")}:${String((i % 4) * 15).padStart(2, "0")}`);

const Toggle = ({ on, onChange, label, hint, testId, disabled }) => (
  <div className="flex items-start justify-between gap-6 py-3">
    <div className="min-w-0">
      <p className="text-[15px] text-tinta" id={`${testId}-label`}>{label}</p>
      {hint && <p className="text-[13px] text-sec mt-0.5">{hint}</p>}
    </div>
    <button type="button" role="switch" aria-checked={on} aria-labelledby={`${testId}-label`} disabled={disabled} onClick={() => onChange(!on)} data-testid={testId}
      className={`shrink-0 relative h-7 w-12 rounded-full border-[1.5px] transition-colors ${on ? "bg-tinta border-tinta" : "bg-hoja border-borde"} disabled:opacity-50`}>
      <span className={`absolute top-[3px] h-4 w-4 rounded-full transition-transform ${on ? "left-[3px] translate-x-5 bg-sobre-tinta" : "left-[3px] bg-borde"}`} />
    </button>
  </div>
);

const Section = ({ title, children, testId }) => (
  <section className="max-w-2xl pt-8 mt-8 border-t border-linea first:mt-0 first:pt-0 first:border-0" data-testid={testId}>
    <h2 className="font-serif text-[26px] leading-tight text-tinta">{title}</h2>
    <div className="mt-3">{children}</div>
  </section>
);

const IosNote = ({ emailOn, onEmail }) => (
  <div className="rounded-[24px] bg-superficie p-5 mt-2" data-testid="ios-install-note">
    <p className="text-[14px] text-tinta">En iPhone, las notificaciones llegan solo si instalas Pensive en tu pantalla de inicio.</p>
    <Link to="/instalar" className="inline-block mt-2 text-[14px] text-acento-texto underline underline-offset-2" data-testid="ios-install-link">Cómo instalarla</Link>
    {!emailOn && (
      <div className="mt-4 pt-4 border-t border-linea flex flex-wrap items-center justify-between gap-3">
        <p className="text-[14px] text-sec">Mientras tanto, puedes recibirlo por correo.</p>
        <button type="button" className="btn-secondary px-4 py-2 text-[14px]" onClick={onEmail} data-testid="ios-use-email-button">Usar el correo</button>
      </div>
    )}
  </div>
);

const InstalarApp = () => {
  const { installed, canPrompt } = usePwa();
  if (installed) return <p className="text-[15px] text-sec" data-testid="install-status">Pensive está instalada en este dispositivo.</p>;
  return (
    <div>
      <p className="text-[15px] text-sec">Ábrela desde tu pantalla de inicio como una app, sin la barra del navegador.</p>
      {canPrompt
        ? <button type="button" className="btn-secondary mt-4" onClick={promptInstall} data-testid="account-install-button">Instalar Pensive</button>
        : <Link to="/instalar" className="inline-block mt-3 text-[15px] text-acento-texto underline underline-offset-2" data-testid="account-install-link">Ver cómo instalarla</Link>}
    </div>
  );
};

const Reminder = () => {
  const { settings, saveSettings } = useRemanso();
  const [note, setNote] = useState("");
  const [iosSteps, setIosSteps] = useState(false);
  if (!settings) return <p className="text-sm text-meta">Cargando…</p>;
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const save = (patch) => saveSettings({ ...patch, timezone: tz }).catch((e) => setNote(errorText(e)));

  const togglePush = async (on) => {
    setNote("");
    if (!on) { await disablePush(); return save({ reminder_push: false }); }
    if (isIOS() && !isStandalone()) { setIosSteps(true); return undefined; }
    if (!pushSupported()) { setNote("Este navegador no permite notificaciones. Puedes usar el recordatorio por correo."); return undefined; }
    try {
      await enablePush(settings.push_public_key);
      await saveSettings({ reminder_push: true, timezone: tz }).catch(async (e) => { await disablePush(); throw e; });
      return undefined;
    } catch (e) {
      setNote(e.message === "denied"
        ? "El navegador no tiene permiso para mostrar notificaciones de Pensive. Puedes dárselo en los ajustes del navegador."
        : "No pudimos activar la notificación en este momento. Intenta de nuevo en un rato.");
    }
    return undefined;
  };

  return (
    <div>
      <p className="text-[15px] text-sec leading-relaxed">Un aviso al día con una sola pregunta: “¿Cómo te fue hoy con tus hábitos?”. No incluye nombres de hábitos ni nada de lo que escribes.</p>
      <div className="mt-3 divide-y divide-linea">
        <Toggle label="Notificación" hint="En este dispositivo, aunque Pensive esté cerrado." on={settings.reminder_push} onChange={togglePush} testId="reminder-push-toggle" />
        <Toggle label="Correo" hint="Con un enlace a Hábitos y otro para dejar de recibirlo." on={settings.reminder_email} onChange={(on) => save({ reminder_email: on })} testId="reminder-email-toggle" />
        <label className="flex items-center justify-between gap-6 py-3">
          <span><span className="block text-[15px] text-tinta">Hora</span><span className="block text-[13px] text-sec mt-0.5">Zona horaria: {tz}</span></span>
          <select className="field w-auto" value={settings.reminder_time} onChange={(e) => save({ reminder_time: e.target.value })} data-testid="reminder-time-select">
            {TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
      </div>
      {iosSteps && <IosNote emailOn={settings.reminder_email} onEmail={() => save({ reminder_email: true })} />}
      {note && <p className="calm-note mt-3" role="status" data-testid="reminder-note">{note}</p>}
    </div>
  );
};

const Exportar = () => {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const download = async () => {
    setBusy(true);
    setNote("");
    try {
      const { data, headers } = await api.get("/export", { responseType: "blob" });
      const name = /filename="([^"]+)"/.exec(headers["content-disposition"] || "")?.[1] || "pensive.txt";
      const url = URL.createObjectURL(data);
      const a = Object.assign(document.createElement("a"), { href: url, download: name });
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      setNote("No pudimos preparar el archivo en este momento. Intenta de nuevo en un rato.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <p className="text-[15px] text-sec">Descarga un archivo de texto con tus entradas, tus hábitos y los días que marcaste.</p>
      <button type="button" className="btn-secondary mt-4" onClick={download} disabled={busy} data-testid="export-button">{busy ? "Preparando…" : "Exportar mis entradas"}</button>
      {note && <p className="calm-note mt-3" role="status" data-testid="export-note">{note}</p>}
    </div>
  );
};

const BorrarCuenta = () => {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [armed, setArmed] = useState(false);
  const [note, setNote] = useState("");
  useEffect(() => {
    if (!armed) return undefined;
    const t = setTimeout(() => setArmed(false), 6000);
    return () => clearTimeout(t);
  }, [armed]);
  const confirm = async () => {
    try {
      await disablePush().catch(() => {});
      await api.delete("/account");
      setUser(false);
      navigate("/entrar", { replace: true });
    } catch {
      setNote("No pudimos borrar tu cuenta en este momento. Tus datos siguen aquí; intenta de nuevo en un rato.");
    }
  };
  return (
    <div>
      <p className="text-[15px] text-sec leading-relaxed">Se borran para siempre tu cuenta, tus entradas, tus hábitos y los días que marcaste. No se puede deshacer. Si quieres conservarlas, exporta primero.</p>
      <button type="button" className={`mt-4 ${armed ? "btn-primary" : "btn-secondary"}`} onClick={() => (armed ? confirm() : setArmed(true))} data-testid="delete-account-button">
        {armed ? "Toca otra vez para borrar todo para siempre" : "Borrar mi cuenta"}
      </button>
      {note && <p className="calm-note mt-3" role="status" data-testid="delete-account-note">{note}</p>}
    </div>
  );
};

export default function MiCuenta() {
  const { settings, saveSettings } = useRemanso();
  return (
    <Hoja testId="screen-mi-cuenta" title="Mi cuenta">
      <Section title="Recordatorio de hábitos" testId="section-reminder"><Reminder /></Section>
      <Section title="Remanso" testId="section-remanso">
        <Toggle label="Sonido suave cuando Remanso se asoma" hint="Un sonido breve cuando aparece una pregunta que no pediste." on={!!settings?.giro_sound}
          disabled={!settings} onChange={(on) => saveSettings({ giro_sound: on }).catch(() => {})} testId="giro-sound-toggle" />
      </Section>
      <Section title="Instalar la app" testId="section-install"><InstalarApp /></Section>
      <Section title="Tus datos" testId="section-data">
        <Exportar />
        <p className="text-[14px] text-sec mt-6">
          <Link to="/privacidad" className="text-acento-texto underline underline-offset-2" data-testid="privacy-link">Privacidad</Link>
          {" · "}
          <Link to="/terminos" className="text-acento-texto underline underline-offset-2" data-testid="terms-link">Términos y condiciones</Link>
        </p>
      </Section>
      <Section title="Borrar cuenta" testId="section-delete"><BorrarCuenta /></Section>
    </Hoja>
  );
}
